import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Complaint } from '../../types/complaint';
import { workflowService } from '../../services/workflow/workflowService';
import { CheckCircle2, XCircle, UserCheck } from 'lucide-react';

interface TriageModalProps {
  isOpen: boolean;
  onClose: () => void;
  complaint: Complaint | null;
  onSuccess: () => void;
}

export const TriageModal: React.FC<TriageModalProps> = ({ isOpen, onClose, complaint, onSuccess }) => {
  const [supervisor, setSupervisor] = useState('Supervisor Suresh');
  const [priority, setPriority] = useState<'Low' | 'Medium' | 'High' | 'Critical'>('High');
  const [remarks, setRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!complaint) return null;

  const handleAction = async (action: 'ACCEPT' | 'REJECT') => {
    setIsSubmitting(true);
    const res = await workflowService.triageComplaint({
      complaint_id: complaint.complaint_id,
      action,
      supervisor_name: action === 'ACCEPT' ? supervisor : undefined,
      priority_name: priority,
      officer_remarks: remarks,
    });

    if (res.success) {
      onSuccess();
      onClose();
    }
    setIsSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Officer Review: ${complaint.complaint_id}`}>
      <div className="space-y-4 text-xs text-slate-300">
        <div className="p-3 bg-slate-950 rounded-lg border border-slate-800">
          <p className="font-semibold text-slate-100 text-sm mb-1">{complaint.title}</p>
          <p className="text-slate-400 leading-relaxed">{complaint.description}</p>
        </div>

        <div>
          <label className="block font-medium text-slate-300 mb-1">Assign Field Supervisor</label>
          <select
            value={supervisor}
            onChange={e => setSupervisor(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100"
          >
            <option value="Supervisor Suresh">Supervisor Suresh (Facilities)</option>
            <option value="Supervisor Vikram">Supervisor Vikram (Electrical)</option>
            <option value="Supervisor Amit">Supervisor Amit (Civil/HVAC)</option>
          </select>
        </div>

        <div>
          <label className="block font-medium text-slate-300 mb-1">Override SLA Priority</label>
          <select
            value={priority}
            onChange={e => setPriority(e.target.value as any)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100"
          >
            <option value="Low">Low (72 hrs SLA)</option>
            <option value="Medium">Medium (24 hrs SLA)</option>
            <option value="High">High (8 hrs SLA)</option>
            <option value="Critical">Critical (4 hrs SLA)</option>
          </select>
        </div>

        <div>
          <label className="block font-medium text-slate-300 mb-1">Officer Instructions / Remarks</label>
          <textarea
            rows={2}
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            placeholder="Instructions for Supervisor or Vendor..."
            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-xs text-slate-100"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
          <Button variant="danger" onClick={() => handleAction('REJECT')} isLoading={isSubmitting} icon={<XCircle className="w-4 h-4" />}>
            Reject Complaint
          </Button>
          <Button variant="primary" onClick={() => handleAction('ACCEPT')} isLoading={isSubmitting} icon={<CheckCircle2 className="w-4 h-4" />}>
            Accept & Assign Supervisor
          </Button>
        </div>
      </div>
    </Modal>
  );
};
